import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  Input,
  Button,
  Typography,
  Alert,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  IconButton,
} from "@material-tailwind/react";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import ResetPasswordUser from "../components/ResetPasswordUser";
import { fetchOrgProfile } from "../api/authService";
import { updateWorkflow } from "../utils/workflowUtils";
import { signupFields } from "../constants/formFields";
import { useMessage } from "../MessageContext";
import { getSignupErrorMessage, getSignupValidationErrors } from "../utils/signupValidation";
import { getReferralAttribution, getPostTrackingToken } from "../utils/referralAttribution";
import { defaultAssignedRole, roleDestination, workspaceForRole } from "../utils/roleNavigation";

const fields = signupFields;

export default function AuthTabs() {
  const msg = useMessage();
  const {
    setIsLoggedIn,
    setIsApproverLoggedIn,
    setActiveWorkspace,
    setActiveRole,
    refreshSessionRoles,
    setProfile,
    apiUrl,
    email,
    setEmail,
    setSubscription_id,
    setSubscriptionCheck,
    setToken,
    setUser_type,
    setApproverId,
    setLoggedInUserId,
    workflow,
    setUser_type_new,
    setLearnMoreById,
    username,
    password,
  } = useAuth();

  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => (
    new URLSearchParams(location.search).get("tab") === "signup" ? "signup" : "login"
  ));

  useEffect(() => {
    setActiveTab(new URLSearchParams(location.search).get("tab") === "signup" ? "signup" : "login");
  }, [location.search]);

  // ---------- Login States ----------
  const [newPassword, setNewPassword] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const invitedOrgId = params.get("organization_id");
    const invitedEmail = params.get("email");
    if (invitedOrgId && /^\d+$/.test(invitedOrgId)) setOrganizationId(invitedOrgId);
    if (invitedEmail) setEmail(invitedEmail);
  }, [location.search, setEmail]);
  const [loginLoading, setLoginLoading] = useState(false);

  // ---------- Signup States ----------
  const [formData, setFormData] = useState({});
  const [signupLoading, setSignupLoading] = useState(false);
  const [isSignupSuccess, setIsSignupSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const signupSubmittingRef = useRef(false);

  // ---------- Shared States ----------
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("");
  const navigate = useNavigate();
  const { instance } = useMsal();

  // 🔹 Alerts auto-hide
  useEffect(() => {
    if (showAlert) {
      const timeoutId = setTimeout(() => {
        setShowAlert(false);
        // if (isSignupSuccess) {
        //   navigate("/home/updatepassword");
        // }
      }, 8000);
      return () => clearTimeout(timeoutId);
    }
  }, [showAlert, isSignupSuccess]);

  // 🔹 Admin Login (common)
  const handleAdminLogin = async (userData, emailValue) => {
    const loginStartedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    setIsLoggedIn(true);
    const fallbackProfile = {
      ...userData,
      ownername: userData?.ownername || emailValue?.split("@")[0] || "there",
      orgname: userData?.orgname || `Organization ${organizationId}`,
    };
    setProfile(fallbackProfile);
    setToken(userData.access_token);
    setApproverId(userData.approver_id || null);
    setLoggedInUserId(userData.approver_id || "");
    setUser_type("admin");
    setUser_type_new("admin");
    // The API is authoritative for this capability. Organization owners are
    // approvers by default, while other users retain their server-calculated
    // approver status.
    setIsApproverLoggedIn(Boolean(userData.is_approver));
    setActiveWorkspace("admin");
    setSubscription_id(organizationId);
    setSubscriptionCheck({ subscriptionId: organizationId, status: "unknown" });

    // Update workflow URLs immediately; profile enrichment is not required
    // to render the authenticated landing page and should not block login.
    const updatedWorkflow = updateWorkflow(workflow, organizationId);
    setLearnMoreById(updatedWorkflow);
    const newOrganizationId = sessionStorage.getItem("newOrganizationOfferOrgId");
    if (newOrganizationId === String(organizationId)) {
      sessionStorage.removeItem("newOrganizationOfferOrgId");
      navigate("/credits");
    } else {
      navigate("/home/organization");
    }
    const navigatedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    console.info(`[perf] admin-login-ui-ready ${Math.round(navigatedAt - loginStartedAt)}ms`);

    // Enrich the fallback profile in the background. The retry preserves
    // resilience for a temporarily unavailable profile endpoint.
    try {
      let profileData;
      for (let attempt = 0; attempt < 2 && !profileData; attempt += 1) {
        try {
          profileData = await fetchOrgProfile({
            apiUrl,
            subscription_id: organizationId,
            email: emailValue,
            token: userData.access_token,
          });
        } catch (error) {
          if (attempt === 1) throw error;
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }
      if (profileData) setProfile({ ...fallbackProfile, ...profileData });
    } catch (error) {
      console.warn("Organization profile enrichment unavailable; using fallback profile.");
    }
  };

  // Azure AD sign-in deliberately exchanges an API-scoped Entra token for the
  // same StunM browser session used by password sign-in. Identity matching and
  // first-login linking happen on the server; do not look users up through
  // Microsoft Graph or send a browser-supplied email to the API.
  const handleLoginAzure = async () => {
    if (!organizationId.trim()) {
      setAlertMessage(msg("auth.orgIdRequired"));
      setAlertVariant("red");
      setShowAlert(true);
      return;
    }
    if (!process.env.REACT_APP_AZURE_AD_API_SCOPE) {
      setAlertMessage(msg("auth.backendError"));
      setAlertVariant("red");
      setShowAlert(true);
      return;
    }

    try {
      setLoginLoading(true);
      const response = await instance.loginPopup(loginRequest);
      const exchangeResponse = await fetch(`${apiUrl}/auth/azure/exchange`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_token: response.accessToken,
          organization_id: Number(organizationId),
        }),
      });
      const result = await exchangeResponse.json().catch(() => ({}));
      if (!exchangeResponse.ok) {
        throw new Error(result?.detail?.message || result?.detail || msg("auth.azureLoginFailed"));
      }

      if (result.user_type === "admin") {
        await handleAdminLogin(result, result.email);
      } else {
        // Keep the non-admin path identical to password login. The HttpOnly
        // cookies are authoritative after a reload, while this token supports
        // existing in-memory Bearer callers during the transition.
        setProfile(result);
        setToken(result.access_token);
        await processUserRole(result);
      }
    } catch (error) {
      console.error("Azure login failed:", error);
      setAlertVariant("red");
      setAlertMessage(error?.message || msg("auth.azureLoginFailed"));
      setShowAlert(true);
    } finally {
      setLoginLoading(false);
    }
  };
  // 🔹 Email/Password Login
  const handleLoginPassword = async (e) => {
    e.preventDefault();
    if (!organizationId.trim()) {
      setAlertMessage(msg("auth.orgIdRequired"));
      setAlertVariant("red");
      setShowAlert(true);
      return;
    }
    try {
      setLoginLoading(true);
      const payload = {
        email,
        password: newPassword,
        organization_id: organizationId,
      };
      const res = await axios.post(`${apiUrl}/api/consumers/login`, payload, {
        withCredentials: true,
      });
      const userData = res.data;

      if (userData.user_type === "admin") {
        await handleAdminLogin(userData, email);
        return;
      }
      setIsLoggedIn(true);
      setProfile(userData);
      setToken(userData.access_token);

      const profileRes = await axios.post(
        `${apiUrl}/api/consumers/profile?email=${encodeURIComponent(
          email
        )}&organization_id=${organizationId}`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${userData.access_token}` },
        }
      );
      await processUserRole({ ...profileRes.data, is_approver: userData.is_approver });
    } 
    catch (err) {
  console.error("Password Login Failed");

  console.error(err);

  if (err.response) {
    console.error(err.response.status);
    console.error(err.response.data);
  }

  const message =
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    msg("auth.genericError");

  setAlertVariant("red");
  setAlertMessage(message);
  setShowAlert(true);
}
    finally {
      setLoginLoading(false);
    }
  };

  // 🔹 Role handler
  const processUserRole = async (data) => {
    const roles = Array.isArray(data.accessconsumer) ? data.accessconsumer : [];
    const selectedRole = defaultAssignedRole(roles, "user");
    const destination = roleDestination(selectedRole);
    if (!destination) {
      setAlertMessage(msg("auth.noValidRole"));
      setAlertVariant("yellow");
      setShowAlert(true);
      return;
    }
    const canApprove = Boolean(data.is_approver) || roles.some((role) => ["CMO/CXO", "Approver"].includes(role));
    setSubscription_id(data.organization_id);
    setSubscriptionCheck({ subscriptionId: data.organization_id, status: "unknown" });
    setLoggedInUserId(data.id);
    setApproverId(data.id);
    setIsApproverLoggedIn(canApprove);
    setUser_type("user");
    setActiveRole(selectedRole);
    setActiveWorkspace(workspaceForRole(selectedRole));
    setIsLoggedIn(true);
    navigate(destination);
    refreshSessionRoles().catch(() => console.warn("Assigned roles will refresh with the session."));
  };

  // 🔹 Signup
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    setFieldErrors((currentErrors) => {
      if (!currentErrors[name]) return currentErrors;
      const remainingErrors = { ...currentErrors };
      delete remainingErrors[name];
      return remainingErrors;
    });
  };
  const handleSignup = async (e) => {
    e.preventDefault();
    if (signupSubmittingRef.current || isSignupSuccess) {
      return;
    }
    signupSubmittingRef.current = true;
    setSignupLoading(true);
    setFieldErrors({});
    let signupSucceeded = false;
    try {
      // const tokenData = new URLSearchParams();
      // tokenData.append("username", username);
      // tokenData.append("password", password);
      // const tokenResponse = await axios.post(`${apiUrl}/token`, tokenData, {
      //   headers: { "Content-Type": "application/x-www-form-urlencoded" },
      // });
      // const accessToken = tokenResponse.data.access_token;

      const { ref, visitorId, visitId } = getReferralAttribution();
      const postTrackingToken = getPostTrackingToken();
      const signupPayload = {
        ...formData,
        ...(ref ? { signup_ref: ref } : {}),
        ...(visitorId ? { signup_visitor_id: visitorId } : {}),
        ...(visitId ? { signup_visit_id: visitId } : {}),
        ...(postTrackingToken ? { signup_post_tracking_token: postTrackingToken } : {}),
      };

      const response = await axios.post(
        `${apiUrl}/api/organizations/`,
        JSON.stringify(signupPayload),
        {
          headers: {
            "Content-Type": "application/json",
            // Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setAlertMessage(
          msg("auth.accountCreatedWithOrgId", {
            orgId: response.data.id,
          })
        );
        setAlertVariant("green");
        setIsSignupSuccess(true);
        signupSucceeded = true;
        setShowAlert(true);
        // Signup does not authenticate this organization. Preserve the current
        // session scope and only prefill the login form with the new ID.
        setOrganizationId(response.data.id);
        sessionStorage.setItem("newOrganizationOfferOrgId", String(response.data.id));
      } else {
        setAlertMessage(msg("auth.genericError"));
        setAlertVariant("red");
        setShowAlert(true);
      }
    } catch (error) {
      console.error("Signup error:", error);
      const validationErrors = getSignupValidationErrors(error);
      const signupErrorMessage = getSignupErrorMessage(error);
      setAlertMessage(
        validationErrors && Object.keys(validationErrors).length
          ? "Please correct the highlighted fields."
          : signupErrorMessage || msg("auth.genericError")
      );
      setAlertVariant("red");
      setShowAlert(true);
      setFieldErrors(validationErrors || {});
    } finally {
      if (!signupSucceeded) {
        signupSubmittingRef.current = false;
      }
      setSignupLoading(false);
    }
  };

  return (
    <div className="flex w-full max-w-md justify-center">
      <Card color="transparent" shadow={false} className="w-full">
        {showAlert && (
          <Alert color={alertVariant} dismissible role="alert" 
           className={
              alertVariant === "success"
                ? "!bg-lightGreen-light !text-lightGreen-dark !border-lightGreen"
                : ""
            }
           > 
            {alertMessage}
          </Alert>
        )}

        <Tabs value={activeTab} className="mt-4 w-full">
          <TabsHeader className="w-full px-0 py-1 text-white bg-white/10" indicatorProps={{ className: "bg-white/30" }}>
            <Tab value="login" onClick={() => setActiveTab("login")}>
              <Typography variant="h6" className="text-white tv:text-xl">
              Login
              </Typography>
            </Tab>
            <Tab value="signup" onClick={() => setActiveTab("signup")}>
              <Typography variant="h6" className="text-white tv:text-xl">
                Signup
              </Typography>
            </Tab>
          </TabsHeader>

          <TabsBody className="w-full">
            {/* 🔹 Login Tab */}
            <TabPanel value="login" className="px-0">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label htmlFor="organization-id" className="text-sm font-semibold text-white">
                    Organization ID
                  </label>
                
                  <Input
                    id="organization-id"
                    name="organizationId"
                    type="text"
                    value={organizationId}
                    onChange={(e) => setOrganizationId(e.target.value)}
                    placeholder="Organization ID"
                    autoComplete="organization"
                    required
                    className="text-white placeholder:text-white/50"
                    color="white"
                  />

                  {/* Azure Login */}
                  <Button
                    className="bg-darkPurple hover:bg-lightPurple capitalize tv:!h-16 tv:text-base"
                    fullWidth
                    onClick={handleLoginAzure}
                    type="button"
                    disabled={loginLoading}
                  >
                    {loginLoading ? "Connecting..." : "Login with Azure AD"}
                  </Button>
                </div>

                {/* <div className="flex justify-center items-center mt-2">
                  <IconButton
                    variant="outlined"
                    onClick={() => setShowResetPassword(!showResetPassword)}
                  >
                    <LockClosedIcon className="h-6 w-6" />
                  </IconButton>
                </div>
                {showResetPassword && <ResetPasswordUser />} */}

                <form className="flex flex-col gap-4" onSubmit={handleLoginPassword}>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="login-email" className="text-sm font-semibold text-white">
                      Email
                    </label>
                    <Input
                      id="login-email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email"
                      autoComplete="email"
                      required
                      className="text-white placeholder:text-white/50"
                      color="white"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="login-password" className="text-sm font-semibold text-white">
                      Password
                    </label>
                    <Input
                      id="login-password"
                      name="password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="********"
                      autoComplete="current-password"
                      required
                      className="text-white placeholder:text-white/50"
                      color="white"
                    />
                  </div>
                  <Button
                    className={`bg-darkPurple hover:bg-lightPurple capitalize tv:!h-16 tv:text-base ${
                      loginLoading && "opacity-50"
                    }`}
                    fullWidth
                    disabled={loginLoading}
                    type="submit"
                  >
                    {loginLoading ? "Connecting..." : "Login"}
                  </Button>
                </form>
              </div>
            </TabPanel>

            {/* 🔹 Signup Tab */}
            <TabPanel value="signup" className="px-0">
              <form className="" onSubmit={handleSignup}>
                {fields.map((field) => (
                  <div key={field.id}>
                    <label htmlFor={field.id} className="text-sm font-semibold text-white">
                      {field.labelText}
                    </label>
                    <Input
                      onChange={handleInputChange}
                      id={field.id}
                      name={field.name}
                      type={field.type}
                      value={formData[field.name] || ""}
                      required={field.isRequired}
                      autoComplete={field.autoComplete}
                      placeholder={field.labelText}
                      className="text-white placeholder:text-white/50"
                      color="white"
                      aria-invalid={Boolean(fieldErrors[field.name])}
                      aria-describedby={fieldErrors[field.name] ? `${field.id}-error` : undefined}
                    />
                    {fieldErrors[field.name] && (
                      <p id={`${field.id}-error`} className="mt-1 text-sm text-red-200" role="alert">
                        {fieldErrors[field.name]}
                      </p>
                    )}
                  </div>
                ))}
                <Button
                  type="submit"
                  className={`mt-6 w-full capitalize bg-darkPurple hover:bg-lightPurple ${
                    signupLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  fullWidth
                  disabled={signupLoading}
                >
                  {signupLoading ? "Creating..." : "Signup"}
                </Button>
              </form>
            </TabPanel>
          </TabsBody>
        </Tabs>
      </Card>
    </div>
  );
}
